import { useState } from 'react';
import Drawer from '@/components/base/Drawer';
import Badge, { statusTone } from '@/components/base/Badge';
import { useAppStore } from '@/store/AppStore';
import { formatTL } from '@/lib/format';
import { jobPaid, jobRemaining } from '@/lib/finance';
import { JOB_STATUSES } from '@/mocks/constants';
import PaymentForm from '@/pages/payments/components/PaymentForm';
import type { Job, JobStatus } from '@/types';

export default function JobDetail({ job, onClose }: { job: Job; onClose: () => void }) {
  const { payments, setJobStatus, addJobNote, addJobTimeline } = useAppStore();
  const [note, setNote] = useState('');
  const [paymentOpen, setPaymentOpen] = useState(false);

  const paid = jobPaid(payments, job.id);
  const remaining = jobRemaining(job, payments);

  function handleNote() {
    if (!note.trim()) return;
    addJobNote(job.id, note.trim());
    setNote('');
  }

  function handleStatus(status: JobStatus) {
    setJobStatus(job.id, status);
    addJobTimeline(job.id, { time: 'Şimdi', text: status });
  }

  return (
    <>
      <Drawer open onClose={onClose} title={job.id} width="max-w-2xl">
        <div className="space-y-6">
          {/* Header / status */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-heading text-lg font-bold text-foreground-950">{job.customerName}</div>
              <div className="text-sm text-foreground-500">{job.service}</div>
            </div>
            <Badge tone={statusTone(job.status)}>{job.status}</Badge>
          </div>

          {/* Contact actions */}
          <div className="flex flex-wrap gap-2">
            <a
              href={`tel:${job.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-phone-line" /> Ara
            </a>
            <a
              href={`https://wa.me/90${job.phone.replace(/\s/g, '').slice(1)}`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-whatsapp-line" /> WhatsApp
            </a>
            <button
              type="button"
              onClick={() => handleStatus('İş Tamamlandı')}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
            >
              <i className="ri-check-line" /> İşi Tamamla
            </button>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Konum</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{job.location}</div>
              <div className="text-xs text-foreground-500">{job.address}</div>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-3">
              <div className="text-xs font-semibold text-foreground-500">Ekip</div>
              <div className="mt-1 text-sm font-semibold text-foreground-900">{job.team}</div>
              <div className="text-xs text-foreground-500">{job.date} · {job.time}</div>
            </div>
          </div>

          {/* Scope */}
          <div>
            <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">İş Kapsamı</h4>
            <p className="rounded-lg border border-background-200 bg-white p-3 text-sm text-foreground-700">
              {job.scope}
            </p>
          </div>

          {/* Financial */}
          {job.jobValue > 0 && (
            <div className="rounded-lg border border-background-200 p-4">
              <h4 className="mb-3 font-heading text-sm font-bold text-foreground-950">Finansal Durum</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs font-semibold text-foreground-500">Hizmet</div>
                  <div className="mt-1 font-heading text-base font-bold text-foreground-900">{formatTL(job.jobValue)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground-500">Tahsil</div>
                  <div className="mt-1 font-heading text-base font-bold text-emerald-600">{formatTL(paid)}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-foreground-500">Kalan</div>
                  <div className={`mt-1 font-heading text-base font-bold ${remaining > 0 ? 'text-amber-600' : 'text-foreground-500'}`}>
                    {formatTL(remaining)}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPaymentOpen(true)}
                className="mt-3 w-full rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-add-line mr-1" /> Ödeme Kaydet
              </button>
            </div>
          )}

          {/* Timeline */}
          <div>
            <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">İş Akışı</h4>
            <ul className="space-y-2">
              {job.timeline.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className="mt-0.5 w-12 shrink-0 text-xs font-semibold text-foreground-400">{t.time}</span>
                  <span className="relative flex-1 border-l border-background-200 pl-4">
                    <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-accent-500" />
                    <span className="text-foreground-700">{t.text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Status update */}
          <div>
            <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Durum Güncelle</h4>
            <select
              className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
              value={job.status}
              onChange={(e) => handleStatus(e.target.value as JobStatus)}
            >
              {JOB_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <h4 className="mb-2 font-heading text-sm font-bold text-foreground-950">Dahili Notlar</h4>
            {job.notes.length > 0 && (
              <ul className="mb-2 space-y-1.5">
                {job.notes.map((n, i) => (
                  <li key={i} className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    “{n}”
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <input
                className="h-10 flex-1 rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Not ekle..."
              />
              <button
                type="button"
                onClick={handleNote}
                className="rounded-md bg-primary-500 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-600"
              >
                Ekle
              </button>
            </div>
          </div>
        </div>
      </Drawer>

      {paymentOpen && (
        <PaymentForm
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          prefillCustomer={job.customerName}
          prefillJobId={job.id}
        />
      )}
    </>
  );
}